/**
 * メンバー詳細コンテンツコンポーネント
 * メンバーKPIトレンド、開発ポイント、リリース詳細を表示
 * /members/$email と /my-page の両方から使用
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business メンバーKPI詳細（トレンドチャート・開発ポイント/リリースモーダル）の共通UIコンポーネント
 * @graph-connects useMemberDetail [calls] メンバー詳細フック
 * @graph-connects useMemberTrend [calls] メンバートレンドフック
 * @graph-connects useMemberDevelopPoints [calls] メンバー開発ポイントフック
 * @graph-connects useMemberReleases [calls] メンバーリリースフック
 * @graph-connects MemberAssignedProjectsSection [calls] 担当要件定義案件一覧 + 担当開発案件一覧のセクション
 * @graph-connects OpenPtCard [calls] 未対応pt カード（基本情報カードの左に配置）
 * @graph-connects MemberOverviewCard [calls] 未対応pt カード隣の 1 枠（基本情報カード / 当月pt 内訳カード）
 * @graph-connects useMemberDevelopPointTargets [calls] 開発pt 自己設定目標線の一覧取得フック
 * @graph-connects DevelopPointsTargetModal [calls] 開発pt 自己設定目標線の設定モーダル（本人のみ）
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardHeader, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import {
  useMemberDetail,
  useMemberTrend,
  useMemberDevelopPoints,
  useMemberReleases,
  useMemberDevelopPointTargets,
} from '../../hooks/useMember.js';
import { useAuth } from '../../hooks/useAuth.js';
import { useMemberProjectHistory } from '../../hooks/useProjects.js';
import { useCombatPowerHistory } from '../../hooks/useCombatPower.js';
import { MemberProjectHistoryTable } from './project/MemberProjectHistoryTable.js';
import { MemberAssignedProjectsSection } from './project/MemberAssignedProjectsSection.js';
import type { MemberProjectHistoryLimit } from '../../types/project.js';
import { ErrorState } from '../feedback/ErrorState/index.js';
import { CombatPowerChart } from './combat-power/index.js';
import { MemberOverviewCard } from './MemberOverviewCard.js';
import { OpenPtCard } from './OpenPtCard.js';
import { MemberQuarterlyKpiTable } from './MemberQuarterlyKpiTable.js';
import { DevelopPointsTrendCard, DEVELOP_POINTS_CHART_HEIGHT } from './DevelopPointsTrendCard.js';
import { DevelopPointsTargetModal } from './DevelopPointsTargetModal.js';
import { extractDateString } from '../../lib/date-utils.js';
import type { DevelopPointTarget } from '../../types/develop-point-target.js';
import { formatPt } from '../../lib/format.js';

/** 未取得時に渡す参照安定な空配列（毎レンダー新しい [] を作らないため） @graph-connects none */
const EMPTY_TARGETS: DevelopPointTarget[] = [];

/** @graph-connects none */
interface MemberDetailContentProps {
  email: string;
  /** ヘッダー部分を非表示にする（マイページで独自ヘッダーを使う場合） */
  hideHeader?: boolean;
}

/** @graph-connects none */
export function MemberDetailContent({ email, hideHeader }: MemberDetailContentProps) {
  const { t } = useTranslation();
  const [selectedDays, setSelectedDays] = useState('90');
  const [targetModalOpen, setTargetModalOpen] = useState(false);
  const [historyLimit, setHistoryLimit] = useState<MemberProjectHistoryLimit>(30);
  const [detailModal, setDetailModal] = useState<{
    open: boolean;
    type: 'developPoints' | 'releases';
    date: string;
    /** 'day' は単日ドリルダウン（日進ビュー）、'range' は選択期間（推移ビュー） */
    mode: 'range' | 'day';
  }>({ open: false, type: 'developPoints', date: '', mode: 'range' });

  const periodOptions = [
    { value: '7', label: t('time.last7Days') },
    { value: '14', label: t('time.last14Days') },
    { value: '30', label: t('time.last30Days') },
    { value: '90', label: t('time.last90Days') },
    { value: '180', label: t('time.last180Days') },
    { value: '365', label: t('time.last1Year') },
  ];

  const {
    data: memberData,
    isLoading: isDetailLoading,
    error: detailError,
  } = useMemberDetail(email);
  const { data: trendData, isLoading: isTrendLoading } = useMemberTrend(email, {
    days: Number(selectedDays),
  });

  const detailParams =
    detailModal.open && detailModal.date
      ? detailModal.mode === 'day'
        ? { startDate: detailModal.date, endDate: detailModal.date }
        : {
            startDate: (() => {
              const date = new Date(detailModal.date);
              date.setDate(date.getDate() - Number(selectedDays));
              return date.toISOString().split('T')[0];
            })(),
            endDate: detailModal.date,
          }
      : null;

  const { data: developPointsData, isLoading: isDevelopPointsLoading } = useMemberDevelopPoints(
    detailModal.type === 'developPoints' ? email : null,
    detailParams,
  );
  const { data: releasesData, isLoading: isReleasesLoading } = useMemberReleases(
    detailModal.type === 'releases' ? email : null,
    detailParams,
  );

  const { data: memberHistoryData, isLoading: isMemberHistoryLoading } = useMemberProjectHistory(
    email,
    historyLimit,
  );

  // 戦闘力サマリー (前月比) は戦闘力チャートと同じ history API から取得
  // (TanStack Query が queryKey 単位で dedupe するため追加リクエストは発生しない)
  const { data: combatHistory } = useCombatPowerHistory(memberData?.member.nickName ?? undefined);

  // 開発pt 推移（日進）の自己設定目標線。閲覧は権限のある全員、設定できるのは本人だけ
  // （API 側でも本人判定するため、ここでのボタン出し分けは UI 上の案内にとどまる）。
  const { user } = useAuth();
  const isSelf = (user?.email ?? '').toLowerCase() === email.toLowerCase();
  const { data: developPointTargetData } = useMemberDevelopPointTargets(email);
  const developPointTargets = developPointTargetData?.targets ?? EMPTY_TARGETS;

  const openDetailModal = (
    date: unknown,
    type: 'developPoints' | 'releases',
    mode: 'range' | 'day' = 'range',
  ) => {
    const normalizedDate = extractDateString(date);
    setDetailModal({ open: true, type, date: normalizedDate, mode });
  };
  const closeDetailModal = () => {
    setDetailModal((prev) => ({ ...prev, open: false }));
  };

  // 月("YYYY/MM") → 期待値戦闘力（= 稼働日あたり期待pt）。日進ビューの目標線に使う
  const expectedByMonth = useMemo(() => {
    const map = new Map<string, number>();
    combatHistory?.months.forEach((m) => {
      if (m.expected !== null) map.set(m.month, m.expected);
    });
    return map;
  }, [combatHistory]);

  if (isDetailLoading) {
    return (
      <div className="h-48 flex items-center justify-center">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (detailError || !memberData?.member) {
    return <ErrorState />;
  }

  const member = memberData.member;

  // ブロック間の余白は親に依存させない（マイページのタブパネルは space-y を持たないため、
  // 親任せだと KPI 表・チャート・案件一覧が密着する）
  return (
    <div className="space-y-6">
      {/* ヘッダー（hideHeaderでなければ表示） */}
      {!hideHeader && (
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-[var(--color-text-primary)]">
              {member.nickName ?? member.name}
            </h2>
            <p className="mt-1 text-[var(--color-text-secondary)]">{member.email}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <span className="inline-flex items-center rounded-full bg-[var(--color-bg-elevated)] px-2.5 py-0.5 text-xs font-medium text-[var(--color-text-secondary)]">
                {member.groupName}
              </span>
              <span className="inline-flex items-center rounded-full bg-[var(--color-bg-elevated)] px-2.5 py-0.5 text-xs font-medium text-[var(--color-text-secondary)]">
                {member.teamName}
              </span>
            </div>
          </div>
          <div className="flex gap-3">
            <Select
              options={periodOptions}
              value={selectedDays}
              onChange={(e) => {
                setSelectedDays(e.target.value);
              }}
              size="sm"
            />
          </div>
        </div>
      )}

      {/* Period selector（hideHeader時はここに表示） */}
      {hideHeader && (
        <div className="flex justify-end">
          <Select
            options={periodOptions}
            value={selectedDays}
            onChange={(e) => {
              setSelectedDays(e.target.value);
            }}
            size="sm"
          />
        </div>
      )}

      {/* 未対応pt カード + 基本情報カード（PDG エンジニアは当月pt 内訳カード）。下段（当月pt
          カード + 四半期KPI カード）と同じ 1:3 の割合で左右に並べ、未対応pt カードが
          当月pt カードの真上に来るように揃える */}
      <div className="flex flex-col md:flex-row gap-6 items-stretch">
        <OpenPtCard email={email} />
        <div className="md:flex-1 min-w-0">
          <MemberOverviewCard
            member={member}
            combat={combatHistory ? { prevMonthRatio: combatHistory.prevMonthRatio } : undefined}
          />
        </div>
      </div>

      {/* 四半期KPI（月別 目標/実績/達成率） */}
      <MemberQuarterlyKpiTable email={email} />

      {/* 戦闘力推移 */}
      <CombatPowerChart nickName={member.nickName} />

      {/* チャート */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 開発ポイント推移（推移 / 日進 切替） */}
        <DevelopPointsTrendCard
          data={trendData?.data}
          isLoading={isTrendLoading}
          expectedByMonth={expectedByMonth}
          wipPt={trendData?.wipPt ?? null}
          onSelectDate={(date, mode) => {
            openDetailModal(date, 'developPoints', mode);
          }}
          customTargets={developPointTargets}
          canEditTarget={isSelf}
          onEditTarget={() => {
            setTargetModalOpen(true);
          }}
        />

        {/* リリース数トレンド */}
        <Card className="animate-content-reveal">
          <CardHeader title={t('members.detail.releaseCountTrend')} />
          <CardContent>
            {/* 左隣の開発ポイント推移カードと縦幅を揃える（grid の行高が片側だけ余るのを防ぐ） */}
            {isTrendLoading ? (
              <div className={`${DEVELOP_POINTS_CHART_HEIGHT} flex items-center justify-center`}>
                <div className="loading-spinner" />
              </div>
            ) : trendData?.data && trendData.data.length > 0 ? (
              <div className={DEVELOP_POINTS_CHART_HEIGHT}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={trendData.data.map((point) => ({
                      date: point.date,
                      value: point.releaseCount,
                    }))}
                    margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                    onClick={
                      ((state: { activeLabel?: string | number }) => {
                        if (state.activeLabel != null) {
                          openDetailModal(state.activeLabel, 'releases');
                        }
                      }) as never
                    }
                    style={{ cursor: 'pointer' }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis
                      dataKey="date"
                      stroke="var(--color-text-muted)"
                      fontSize={11}
                      tickFormatter={(value: string) => {
                        const d = new Date(extractDateString(value));
                        return isNaN(d.getTime())
                          ? extractDateString(value)
                          : `${String(d.getMonth() + 1)}/${String(d.getDate())}`;
                      }}
                    />
                    <YAxis
                      stroke="var(--color-text-muted)"
                      fontSize={11}
                      tickFormatter={(v: number) => String(v)}
                    />
                    <Tooltip
                      wrapperStyle={{ zIndex: 50, outline: 'none' }}
                      contentStyle={{
                        backgroundColor: 'var(--color-bg-elevated)',
                        border: '1px solid var(--color-border)',
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-lg)',
                      }}
                      labelStyle={{ color: 'var(--color-text-primary)' }}
                      formatter={
                        ((value: number) => [
                          value.toLocaleString(),
                          t('members.fields.releaseCount30d'),
                        ]) as never
                      }
                      labelFormatter={
                        ((label: string) => {
                          const d = new Date(extractDateString(label));
                          return isNaN(d.getTime())
                            ? extractDateString(label)
                            : d.toLocaleDateString();
                        }) as never
                      }
                    />
                    <Bar
                      dataKey="value"
                      fill="var(--color-info-muted)"
                      stroke="var(--color-info)"
                      strokeWidth={1}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div
                className={`${DEVELOP_POINTS_CHART_HEIGHT} flex items-center justify-center border border-dashed border-[var(--color-border)] rounded-lg`}
              >
                <p className="text-[var(--color-text-muted)]">{t('status.noData')}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* 担当要件定義案件一覧（上） + 担当開発案件一覧（下） */}
      <MemberAssignedProjectsSection email={email} alignReadyKey={memberData} />

      {/* 担当案件履歴（完了案件を着手日の新しい順に追跡） */}
      <Card className="animate-content-reveal">
        <CardHeader
          title={t('members.detail.assignedHistory')}
          action={
            <Select
              options={[30, 50, 100].map((n) => ({
                value: String(n),
                label: t('members.detail.historyLimitOption', { count: n }),
              }))}
              value={String(historyLimit)}
              onChange={(e) => {
                setHistoryLimit(Number(e.target.value) as MemberProjectHistoryLimit);
              }}
              size="sm"
              aria-label={t('members.detail.historyLimit')}
            />
          }
        />
        <CardContent>
          {isMemberHistoryLoading ? (
            <div className="h-48 flex items-center justify-center">
              <div className="loading-spinner" />
            </div>
          ) : memberHistoryData?.data ? (
            <MemberProjectHistoryTable projects={memberHistoryData.data} />
          ) : (
            <p className="text-center py-8 text-[var(--color-text-muted)]">
              {t('members.detail.noHistory')}
            </p>
          )}
        </CardContent>
      </Card>

      {/* 詳細モーダル */}
      <Modal
        open={detailModal.open}
        onClose={closeDetailModal}
        title={
          detailModal.type === 'developPoints'
            ? t('members.detail.developPoints')
            : t('members.detail.releases')
        }
        size="lg"
      >
        <div className="space-y-4">
          <p className="text-sm text-[var(--color-text-secondary)]">
            {detailModal.type === 'developPoints'
              ? detailModal.mode === 'day'
                ? t('members.detail.developPointsDayDescription', {
                    name: member.nickName ?? member.name,
                    date: detailModal.date,
                  })
                : t('members.detail.developPointsDescription', {
                    name: member.nickName ?? member.name,
                    date: detailModal.date,
                    days: selectedDays,
                  })
              : t('members.detail.releasesDescription', {
                  name: member.nickName ?? member.name,
                  date: detailModal.date,
                  days: selectedDays,
                })}
          </p>

          {isDevelopPointsLoading || isReleasesLoading ? (
            <div className="h-48 flex items-center justify-center">
              <div className="loading-spinner" />
            </div>
          ) : detailModal.type === 'developPoints' && developPointsData?.data ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[var(--color-border)]">
                    <th className="text-left py-2 px-3 text-sm font-medium text-[var(--color-text-muted)]">
                      {t('kpi.detail.completedDate')}
                    </th>
                    <th className="text-left py-2 px-3 text-sm font-medium text-[var(--color-text-muted)]">
                      {t('kpi.detail.issueKey')}
                    </th>
                    <th className="text-left py-2 px-3 text-sm font-medium text-[var(--color-text-muted)]">
                      {t('kpi.detail.summary')}
                    </th>
                    <th className="text-right py-2 px-3 text-sm font-medium text-[var(--color-text-muted)]">
                      {t('kpi.detail.developPoint')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {developPointsData.data.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-[var(--color-text-muted)]">
                        {t('kpi.detail.noItems')}
                      </td>
                    </tr>
                  ) : (
                    developPointsData.data.map((item, index) => (
                      <tr
                        key={`${item.issueKey}-${String(index)}`}
                        className="border-b border-[var(--color-border)] last:border-b-0"
                      >
                        <td className="py-2 px-3 text-sm text-[var(--color-text-secondary)]">
                          {extractDateString(item.completedDate)}
                        </td>
                        <td className="py-2 px-3 text-sm text-[var(--color-accent-primary)]">
                          {item.issueKey}
                        </td>
                        <td className="py-2 px-3 text-sm text-[var(--color-text-primary)] max-w-xs truncate">
                          {item.summary}
                        </td>
                        <td className="py-2 px-3 text-sm text-[var(--color-text-primary)] text-right font-medium">
                          {formatPt(item.developPoint)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          ) : detailModal.type === 'releases' && releasesData?.data ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[var(--color-border)]">
                    <th className="text-left py-2 px-3 text-sm font-medium text-[var(--color-text-muted)]">
                      {t('kpi.detail.releasedDate')}
                    </th>
                    <th className="text-left py-2 px-3 text-sm font-medium text-[var(--color-text-muted)]">
                      {t('kpi.detail.issueKey')}
                    </th>
                    <th className="text-left py-2 px-3 text-sm font-medium text-[var(--color-text-muted)]">
                      {t('kpi.detail.summary')}
                    </th>
                    <th className="text-right py-2 px-3 text-sm font-medium text-[var(--color-text-muted)]">
                      {t('kpi.detail.effectivePoint')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {releasesData.data.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-[var(--color-text-muted)]">
                        {t('kpi.detail.noItems')}
                      </td>
                    </tr>
                  ) : (
                    releasesData.data.map((item, index) => (
                      <tr
                        key={`${item.issueKey}-${String(index)}`}
                        className="border-b border-[var(--color-border)] last:border-b-0"
                      >
                        <td className="py-2 px-3 text-sm text-[var(--color-text-secondary)]">
                          {extractDateString(item.releasedDate)}
                        </td>
                        <td className="py-2 px-3 text-sm text-[var(--color-accent-primary)]">
                          {item.issueKey}
                        </td>
                        <td className="py-2 px-3 text-sm text-[var(--color-text-primary)] max-w-xs truncate">
                          {item.summary}
                        </td>
                        <td className="py-2 px-3 text-sm text-[var(--color-text-primary)] text-right font-medium">
                          {formatPt(item.effectivePoint)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </Modal>

      {/* 開発pt 自己設定目標線の設定モーダル（本人のみ開ける） */}
      {isSelf && (
        <DevelopPointsTargetModal
          open={targetModalOpen}
          onClose={() => {
            setTargetModalOpen(false);
          }}
          email={email}
          targets={developPointTargets}
        />
      )}
    </div>
  );
}
